/**
 * Shaken Baby Syndrome / Abusive Head Trauma Prevention Policy Component
 * Policy acknowledgment form for staff compliance
 * Based on: How to Identify & Prevent Shaken Baby Syndrome, Abusive Head Trauma & Child Maltreatment Policy
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import {
  ShieldAlert, FileText, AlertTriangle, CheckCircle2, 
  Save, Printer, History, Download, Users, Baby
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Policy sections for display
const POLICY_SECTIONS = [
  {
    id: "definition",
    title: "What is Shaken Baby Syndrome / Abusive Head Trauma?",
    content: `Shaken Baby Syndrome (SBS), also known as Abusive Head Trauma (AHT), is a serious brain injury caused by forcefully shaking an infant or young child. It can result in severe brain damage, permanent disabilities, or death.

Key facts:
• Most victims are under 1 year old
• It can happen in seconds
• The injuries are not from normal play or minor falls
• There is NEVER a reason to shake a baby`
  },
  {
    id: "signs",
    title: "Signs and Symptoms",
    content: `Warning signs that may indicate SBS/AHT include:
• Extreme irritability or difficulty consoling
• Difficulty staying awake or lethargy
• Poor feeding or vomiting
• Difficulty breathing
• Seizures
• Inability to lift head
• Inability to focus eyes or track movement
• Unequal pupil size
• Bruises on body (especially head, neck, or back)

If you observe any of these signs, seek immediate medical attention.`
  },
  {
    id: "prevention",
    title: "Prevention Strategies",
    content: `To prevent SBS/AHT:
• Never shake a baby for any reason
• Learn safe ways to calm a crying baby
• Take breaks when feeling frustrated
• Put the baby in a safe place and walk away if overwhelmed
• Ask for help from another caregiver
• Use the "Period of PURPLE Crying" strategies
• Ensure all caregivers understand the dangers of shaking`
  },
  {
    id: "coping",
    title: "Coping with Infant Crying",
    content: `Healthy coping strategies when a baby won't stop crying:
• Check for basic needs (hunger, diaper, temperature)
• Try gentle rocking, swaddling, or soft sounds
• Take the baby for a walk or car ride
• Use a pacifier
• Sing or talk softly to the baby
• Place the baby safely in the crib and take a short break
• Call a friend, family member, or helpline for support

Remember: Crying is normal. Shaking is NEVER okay.`
  },
  {
    id: "reporting",
    title: "Reporting Suspected Abuse",
    content: `As childcare professionals, we are mandated reporters. You MUST report:
• Any suspected child abuse or neglect
• Unexplained injuries
• Behavioral changes that suggest abuse

How to report:
• Document observations objectively
• Report to your supervisor immediately
• Contact Child Protective Services
• Do not investigate on your own

Protecting children is everyone's responsibility.`
  }
];

export const ShakenBabyPolicyForm = ({ centerName = "", organization = {}, staff = [], onSave }) => {
  const [acknowledgments, setAcknowledgments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form");

  // Form state
  const [formData, setFormData] = useState({
    staffId: "",
    staffName: "",
    position: "",
    acknowledgedSections: [],
    understandPolicy: false,
    agreeToComply: false,
    signatureName: "",
    signatureDate: new Date().toISOString().split('T')[0],
    centerName: centerName || organization?.name || ""
  });

  useEffect(() => {
    fetchAcknowledgments();
  }, []);

  useEffect(() => {
    if (centerName || organization?.name) {
      setFormData(prev => ({
        ...prev,
        centerName: centerName || organization?.name || prev.centerName
      }));
    }
  }, [centerName, organization]);

  const fetchAcknowledgments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shaken-baby-policy-acknowledgments`);
      setAcknowledgments(response.data || []);
    } catch (error) {
      console.error("Error fetching acknowledgments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStaffSelect = (staffId) => {
    const selectedStaff = staff.find(s => s.id === staffId);
    if (selectedStaff) {
      setFormData(prev => ({
        ...prev,
        staffId,
        staffName: selectedStaff.name,
        position: selectedStaff.role || selectedStaff.position || ""
      }));
    }
  };

  const handleSectionAcknowledge = (sectionId, checked) => {
    setFormData(prev => ({
      ...prev,
      acknowledgedSections: checked
        ? [...prev.acknowledgedSections, sectionId]
        : prev.acknowledgedSections.filter(s => s !== sectionId)
    }));
  };

  const allSectionsAcknowledged = POLICY_SECTIONS.every(s => 
    formData.acknowledgedSections.includes(s.id)
  );

  const handleSave = async () => {
    if (!formData.staffName || !formData.signatureName) {
      toast.error("Please fill in required fields: Staff Name and Signature");
      return;
    }

    if (!allSectionsAcknowledged) {
      toast.error("Please acknowledge all policy sections");
      return;
    }

    if (!formData.understandPolicy || !formData.agreeToComply) {
      toast.error("Please confirm that you understand the policy and agree to comply");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/shaken-baby-policy-acknowledgments`, formData);
      toast.success("Policy acknowledgment saved successfully!");
      setAcknowledgments(prev => [response.data, ...prev]);
      
      // Reset form
      setFormData({
        staffId: "",
        staffName: "",
        position: "",
        acknowledgedSections: [],
        understandPolicy: false,
        agreeToComply: false,
        signatureName: "",
        signatureDate: new Date().toISOString().split('T')[0],
        centerName: centerName || organization?.name || ""
      });
      
      onSave?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save acknowledgment");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-red-200 bg-gradient-to-br from-red-50 to-orange-50 shadow-lg print:shadow-none">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between print:block">
            <div>
              <CardTitle className="text-2xl text-red-800 flex items-center gap-3">
                <ShieldAlert className="w-7 h-7" />
                Shaken Baby Syndrome / AHT Prevention Policy
              </CardTitle>
              <CardDescription className="text-red-600 mt-1">
                Staff acknowledgment form for child maltreatment prevention policy
              </CardDescription>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setView(view === "form" ? "history" : "form")}
                className="border-red-300"
              >
                {view === "form" ? <History className="w-4 h-4 mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                {view === "form" ? "History" : "Form"}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} className="border-red-300">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>

        {view === "form" ? (
          <CardContent className="space-y-6">
            {/* Staff Selection */}
            <div className="bg-white p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Staff Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {staff.length > 0 ? (
                  <div>
                    <Label className="text-gray-700">Select Staff Member</Label>
                    <Select value={formData.staffId} onValueChange={handleStaffSelect}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select staff..." />
                      </SelectTrigger>
                      <SelectContent>
                        {staff.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="text-gray-700">Staff Name *</Label>
                    <Input
                      value={formData.staffName}
                      onChange={(e) => handleChange("staffName", e.target.value)}
                      className="mt-1"
                      placeholder="Enter staff name"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-gray-700">Name *</Label>
                  <Input
                    value={formData.staffName}
                    onChange={(e) => handleChange("staffName", e.target.value)}
                    className="mt-1"
                    placeholder="Staff name"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Position/Title</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => handleChange("position", e.target.value)}
                    className="mt-1"
                    placeholder="e.g., Teacher, Aide"
                  />
                </div>
              </div>
            </div>

            {/* Policy Sections */}
            <div className="bg-white p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Policy Information
                <Badge variant="outline" className="ml-2">
                  {formData.acknowledgedSections.length}/{POLICY_SECTIONS.length} sections reviewed
                </Badge>
              </h3>

              <Accordion type="single" collapsible className="space-y-2">
                {POLICY_SECTIONS.map((section) => (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className={`border rounded-lg ${
                      formData.acknowledgedSections.includes(section.id) 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <AccordionTrigger className="px-4 hover:no-underline">
                      <div className="flex items-center gap-3">
                        {formData.acknowledgedSections.includes(section.id) ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        )}
                        <span className="font-medium text-left">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="bg-white p-4 rounded border text-sm whitespace-pre-line mb-4">
                        {section.content}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`ack-${section.id}`}
                          checked={formData.acknowledgedSections.includes(section.id)}
                          onCheckedChange={(checked) => handleSectionAcknowledge(section.id, checked)}
                        />
                        <Label htmlFor={`ack-${section.id}`} className="text-sm cursor-pointer">
                          I have read and understand this section
                        </Label>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Acknowledgment Checkboxes */}
            <div className="bg-white p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-4">Acknowledgment & Agreement</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="understand"
                    checked={formData.understandPolicy}
                    onCheckedChange={(checked) => handleChange("understandPolicy", checked)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="understand" className="cursor-pointer">
                    I understand the dangers of Shaken Baby Syndrome / Abusive Head Trauma and the signs and symptoms to watch for in children.
                  </Label>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="comply"
                    checked={formData.agreeToComply}
                    onCheckedChange={(checked) => handleChange("agreeToComply", checked)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="comply" className="cursor-pointer">
                    I agree to follow all prevention strategies outlined in this policy, to never shake a child, and to report any suspected abuse as required by law.
                  </Label>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="bg-white p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-4">Signature</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Signature (Print Name) *</Label>
                  <Input
                    value={formData.signatureName}
                    onChange={(e) => handleChange("signatureName", e.target.value)}
                    className="mt-1"
                    placeholder="Print your full name"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Date</Label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => handleChange("signatureDate", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end print:hidden">
              <Button
                onClick={handleSave}
                disabled={saving || !allSectionsAcknowledged || !formData.understandPolicy || !formData.agreeToComply}
                className="bg-red-600 hover:bg-red-700 text-white px-8"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Submit Acknowledgment
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        ) : (
          <CardContent>
            {/* History View */}
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : acknowledgments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No acknowledgments recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {acknowledgments.map((ack) => (
                  <div key={ack.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div>
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-gray-800">{ack.staffName}</span>
                        <Badge variant="outline" className="text-xs">{ack.position}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Signed: {new Date(ack.signatureDate).toLocaleDateString()} | 
                        All {POLICY_SECTIONS.length} sections acknowledged
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 border-green-300">
                      Complete
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ShakenBabyPolicyForm;
